/* eslint-disable max-len */
import {
  Button,
  Grid,
  Typography
} from '@mui/material';
import { Stack } from '@mui/system';
import { ContainerForm } from 'components/shared/container_form';
import InputComLabelFormik from 'components/shared/custom_input_label_formik';
import DocumentComponent from 'components/shared/document_component';
import { PageTitle } from 'components/shared/page_title';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';



interface Step1Props {
  onNext: () => void;
  formik: any;
}

const Step1: React.FC<Step1Props> = ({ onNext, formik }) => {

  const navigate = useNavigate();
  const location = useLocation();

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = /cifa-(\d{4})/.exec(location.pathname);

  const anoReferencia = match ? Number(match[1]) : 2026;

  const handleNext = () => {
    onNext();
  };

  // Formatar valor para exibição como moeda brasileira
  const formatarValorParaExibicao = (valor: number | string | null | undefined) => {
    if (!valor && valor !== 0) return '';

    // Se for string, converte para número
    const numeroValor = typeof valor === 'string' ? parseFloat(valor) : valor;

    if (isNaN(numeroValor)) return '';

    return numeroValor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };




  return (
    <>
      <PageTitle
        title='Valores para Aquisição de Alimentação Escolar - Em 2026'
        subtitle='Informe corretamente os dados sobre os valores para aquisição de alimentação escolar em 2026.'
      />

      <Stack marginX='4rem' marginTop='1rem' spacing='2rem' flexDirection='column' maxWidth='1152px' width='100%'>



        <ContainerForm title='Valores Aplicados nas Aquisições da Agricultura Familiar'>

          <Grid container spacing={3} sx={{ width: '100%', mb: 3 }}>
            <Grid item xs={12} md={4}>

              <InputComLabelFormik
                label='alimentacao_af_recurso_pnae'
                labelTexto='Recurso do PNAE Federal (R$)'
                formik={formik}
                placeholder='0,00'
                required={true}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_af_recurso_estado'
                labelTexto='Recurso do Governo do Estado (R$) - Opcional'
                formik={formik}
                placeholder='0,00'
                required={false}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_af_recurso_prefeitura'
                labelTexto='Recurso da Prefeitura (R$)'
                formik={formik}
                placeholder='0,00'
                required={true}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 3, pt: 2, }}>
              <Typography variant='body1' color='text.primary' sx={{ fontWeight: 500 }}>
                Total de Recursos Aplicados nas Aquisições da Agricultura Familiar:
                <Typography component='span' variant='h6' color='primary' sx={{ ml: 1, fontWeight: 600 }}>
                  R$ {formatarValorParaExibicao(Number(formik.values.alimentacao_af_recurso_pnae || 0) + Number(formik.values.alimentacao_af_recurso_estado || 0) + Number(formik.values.alimentacao_af_recurso_prefeitura || 0))}
                </Typography>
              </Typography>
            </Grid>
          </Grid>

        </ContainerForm>

        <ContainerForm title='Valores Totais Aplicados na Alimentação Escolar'>

          <Grid container spacing={3} sx={{ width: '100%', mb: 3 }}>
            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_total_recurso_pnae'
                labelTexto='Total de Recursos PNAE Federal (R$)'
                formik={formik}
                placeholder='0,00'
                required={true}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_total_recurso_estado'
                labelTexto='Total de Recursos do Governo do Estado (R$) - Opcional'
                formik={formik}
                placeholder='0,00'
                required={false}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <InputComLabelFormik
                label='alimentacao_total_recurso_prefeitura'
                labelTexto='Total de Recursos da Prefeitura (R$)'
                formik={formik}
                placeholder='0,00'
                required={true}
                version='primary'
                mask='dinheiro'
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 3, pt: 2 }}>
              <Typography variant='body1' color='text.primary' sx={{ fontWeight: 500 }}>
                Total Geral de Recursos na Alimentação Escolar:
                <Typography component='span' variant='h6' color='primary' sx={{ ml: 1, fontWeight: 600 }}>
                  R$ {formatarValorParaExibicao(Number(formik.values.alimentacao_total_recurso_pnae || 0) + Number(formik.values.alimentacao_total_recurso_estado || 0) + Number(formik.values.alimentacao_total_recurso_prefeitura || 0))}
                </Typography>
              </Typography>
            </Grid>
          </Grid>

        </ContainerForm>

        <ContainerForm title='Ata de Aprovação dos Dados de Alimentação Escolar'>

          <Grid container spacing={3} sx={{ width: '100%' }}>
            <Grid item xs={12}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Faça o upload da ata da reunião que aprova os dados de alimentação escolar informados acima.
              </Typography>

              <DocumentComponent
                formik={formik}
                formikLabel='alimentacao_ata_aprovacao_uuid'
                key="ata-aprovacao-alimentacao-component"
                uuid={formik.values.alimentacao_ata_aprovacao_uuid}
                setUuid={(uuid) => formik.setFieldValue('alimentacao_ata_aprovacao_uuid', uuid)}
                podeSubstituir={true}
                title="Ata de Aprovação - Alimentação Escolar"
              />
            </Grid>
          </Grid>

        </ContainerForm>

        <Stack
          direction='row'
          spacing={2}
          justifyContent='center'
          alignItems='center'
          marginTop='2rem'
        >
          <Button variant='text' color='primary'
            onClick={() => {
              navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
            }}
          >
            Voltar
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={handleNext}
          >
            Avançar
          </Button>
        </Stack>
      </Stack>
    </>
  );
};

export default Step1;
